// --------------------------------------------------------------
// Created On: 2026-05-12
// Author: Hannah Vaughan
//
// Last Modified: 2026-05-27
// Modified By: Hannah Vaughan
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useState } from "react";
import styles from "./SubscribeUserGroupModal.module.scss";
import Modal from "src/components/Modal/Modal";
import ModalHeader from "src/components/ModalHeader/ModalHeader";
import ModalBody from "src/components/ModalBody/ModalBody";
import ModalFooter from "src/components/ModalFooter/ModalFooter";

// Modal for selecting escalation/delivery rules for all users in a user group for an alert threshold.
export default function SubscribeUserGroupModal(props: Props): Component {
  const [userGroupName, setUserGroupName] = useState<string>("");
  const [includeEmail, setIncludeEmail] = useState<boolean>(false);
  const [includeText, setIncludeText] = useState<boolean>(false);
  const [includeCall, setIncludeCall] = useState<boolean>(false);
  const [escalationLevel, setEscalationLevel] = useState<number>(0);

  // Closes the modal, then subscribes all users in the selected user group to the current alert threshold.
  function subscribeUserGroup(
    userGroupName: string,
    includeEmail: boolean,
    includeText: boolean,
    includeCall: boolean,
    escalationLevel: number
  ): void {
    props.onSubmit(userGroupName, includeEmail, includeText, includeCall, escalationLevel);
    exitModal();
  }

  // Clears the modal then closes it.
  function exitModal(): void {
    setUserGroupName("");
    setIncludeEmail(false);
    setIncludeText(false);
    setIncludeCall(false);
    setEscalationLevel(0);
    props.onClose();
  }

  return (
    <Fragment>
      <Modal
        show={props.showModal}
        onHide={() => props.onClose()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--super-modal-z-index)" }}
        size="xl"
        animation
        centered
      >
        <ModalHeader>
          <h5 data-test="alert-threshold-subscribe-user-group-modal-header" className="font-weight-bold">
            Subscribe User Group
          </h5>
        </ModalHeader>

        <ModalBody>
          <div className={`${styles.body} mx-2 my-3`} data-test="alert-threshold-subscribe-user-group-modal-body">
            <div className="row align-items-center justify-content-center m-2">
              <div className="col-12">
                <input
                  data-test="manage-alert-threshold-subscribed-user-group-name-input"
                  className="form-control"
                  type="text"
                  list="datalist-user-groups"
                  autoComplete="off"
                  value={userGroupName}
                  disabled={props.isRental}
                  onChange={(e) => setUserGroupName(e.target.value)}
                />
              </div>
            </div>

            <div className="row align-items-center m-2">
              <div className="col-2 text-center">
                <label onClick={() => setIncludeEmail((prev) => !prev)}>Email</label>
                <br />
                <input
                  data-test="manage-alert-threshold-sub-group-by-email-checkbox"
                  className={`form-check-input ${styles.check}`}
                  type="checkbox"
                  checked={includeEmail}
                  disabled={props.isRental}
                  onChange={() => setIncludeEmail((prev) => !prev)}
                />
              </div>

              <div className="col-2 text-center">
                <label onClick={() => setIncludeText((prev) => !prev)}>Text</label>
                <br />
                <input
                  data-test="manage-alert-threshold-sub-group-by-text-checkbox"
                  className={`${styles.check} form-check-input`}
                  type="checkbox"
                  checked={includeText}
                  disabled={props.isRental}
                  onChange={() => setIncludeText((prev) => !prev)}
                />
              </div>

              <div className="col-2 text-center">
                <label onClick={() => setIncludeCall((prev) => !prev)}>Call</label>
                <br />
                <input
                  data-test="manage-alert-threshold-sub-group-by-call-checkbox"
                  className={`form-check-input ${styles.check}`}
                  type="checkbox"
                  checked={includeCall}
                  disabled={props.isRental}
                  onChange={() => setIncludeCall((prev) => !prev)}
                />
              </div>

              <div className="col-6 text-center">
                <label className="mb-2">Alert delivery delay</label>
                <select
                  data-test="manage-alert-threshold-user-group-alert-delivery-delay-selector"
                  className="form-select"
                  value={String(escalationLevel)}
                  disabled={props.isRental}
                  onChange={(e) => setEscalationLevel(parseInt(e.target.value, 10))}
                >
                  <option value="0">No delay</option>
                  <option value="1">5 minutes</option>
                  <option value="2">10 minutes</option>
                  <option value="3">15 minutes</option>
                  <option value="4">20 minutes</option>
                  <option value="5">25 minutes</option>
                  <option value="6">30 minutes</option>
                </select>
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          {props.isRental ? (
            <button
              data-test="alert-threshold-subscribe-user-group-modal-close-button"
              className="btn btn-secondary"
              type="button"
              onClick={() => exitModal()}
            >
              Close
            </button>
          ) : (
            <Fragment>
              <button
                data-test="alert-threshold-subscribe-user-group-modal-subscribe-users-button"
                className="btn btn-primary"
                type="button"
                onClick={() =>
                  subscribeUserGroup(userGroupName, includeEmail, includeText, includeCall, escalationLevel)
                }
              >
                Subscribe Users
              </button>

              <button
                data-test="alert-threshold-subscribe-user-group-modal-cancel-button"
                className="btn btn-secondary"
                type="button"
                onClick={() => exitModal()}
              >
                Cancel
              </button>
            </Fragment>
          )}
        </ModalFooter>
      </Modal>
    </Fragment>
  );
}

interface Props {
  showModal: boolean;
  userGroups: UserGroup[];
  isRental: boolean;
  onSubmit: (
    userGroupName: string,
    includeEmail: boolean,
    includeText: boolean,
    includeCall: boolean,
    escalationLevel: number
  ) => void;
  onClose: () => void;
}

interface UserGroup {
  usergroupId: number;
  name: string;
  userIds: number[];
}
